import { Button, ButtonGroup, type SxProps, type Theme } from '@mui/material'

export interface ToggleOption<T extends string> {
  value: T
  label: string
}

interface Props<T extends string> {
  options: Array<ToggleOption<T>>
  selectedValue: T
  onSelectionChange: (value: T) => void
  dataTestId?: string
  backgroundColor?: string
}

export const StyledToggle = <T extends string>(props: Props<T>): JSX.Element => {
  const mainColor = props.backgroundColor ?? 'var(--blue-700)'
  const containerStyles: SxProps<Theme> = {
    backgroundColor: mainColor,
    color: 'var(--white)',
    p: '6px 5px',
    borderRadius: '4px',
    '& > .MuiButton-root': {
      fontFamily: 'Barlow',
      fontWeight: 700,
      p: '8px 16px',
      boxShadow: 'none',
      borderRadius: '4px !important'
    },
    '& > .MuiButton-root:first-of-type': {
      borderRight: 0
    }
  }

  const buttonStyles: Record<string, SxProps<Theme>> = {
    base: {
      borderRight: '0 !important',
      backgroundColor: 'inherit',
      '&:hover': {
        backgroundColor: 'inherit'
      }
    },
    active: {
      borderRight: '0',
      backgroundColor: 'var(--white)',
      color: mainColor,
      '&:hover': {
        backgroundColor: 'var(--white)'
      }
    }
  }

  const getButtonStyle = (isActive: boolean): SxProps<Theme> => isActive
    ? buttonStyles.active
    : buttonStyles.base

  return <ButtonGroup variant='contained' sx={containerStyles} data-testid={props.dataTestId}>
    {props.options.map(option => (
      <Button
        data-testid={`${option.value}Button`}
        key={option.value}
        sx={getButtonStyle(props.selectedValue === option.value)}
        onClick={() => { props.onSelectionChange(option.value) }}
        aria-pressed={props.selectedValue === option.value}
      >{option.label}</Button>
    ))}
  </ButtonGroup>
}
